'use client';

import { Button, Modal } from 'antd';
import { Check, Copy, XCircle } from 'lucide-react';
import React, { useCallback, useEffect, useState } from 'react';
import { createPortal } from 'react-dom';

export interface ErrorModalOptions {
  title?: string;
  message?: string;
  detail?: string;
  traceId?: string;
}

interface ErrorModalState extends ErrorModalOptions {
  isOpen: boolean;
  /** @internal 多条错误的去重与序号生成 */
  _msgList?: string[];
}

let setGlobalErrorModalState: React.Dispatch<
  React.SetStateAction<ErrorModalState>
> | null = null;

let pendingErrors: ErrorModalOptions[] = [];

const generateTraceId = () => {
  if (typeof crypto !== 'undefined' && crypto.randomUUID) {
    return crypto.randomUUID();
  }
  return `${Date.now()}-${Math.random().toString(36).slice(2, 10)}`;
};

/** 格式化 detail 内容，如果是 JSON 则美化输出 */
const formatDetail = (detail?: string): string => {
  if (!detail) return '';
  try {
    const parsed = JSON.parse(detail);
    return JSON.stringify(parsed, null, 2);
  } catch {
    return detail;
  }
};

const ErrorModalContent: React.FC = () => {
  const [copied, setCopied] = useState(false);
  const [state, setState] = useState<ErrorModalState>({ isOpen: false });

  useEffect(() => {
    setGlobalErrorModalState = setState;
    // 冲刷 Provider 挂载前暂存的错误
    if (pendingErrors.length) {
      const queue = pendingErrors;
      pendingErrors = [];
      queue.forEach((options) => ErrorModal.show(options));
    }
    return () => {
      setGlobalErrorModalState = null;
    };
  }, []);

  const handleClose = useCallback(() => {
    setState((prev) => ({ ...prev, isOpen: false }));
  }, []);

  const fallbackCopy = useCallback((text: string) => {
    const textArea = document.createElement('textarea');
    textArea.value = text;
    textArea.style.cssText = 'position:fixed;left:-999999px;top:-999999px;';
    document.body.appendChild(textArea);
    textArea.focus();
    textArea.select();
    try {
      document.execCommand('copy');
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } finally {
      document.body.removeChild(textArea);
    }
  }, []);

  const handleCopy = useCallback(async () => {
    const textToCopy = formatDetail(state.detail) || state.message || '未知错误';
    if (navigator.clipboard?.writeText) {
      try {
        await navigator.clipboard.writeText(textToCopy);
        setCopied(true);
        setTimeout(() => setCopied(false), 2000);
        return;
      } catch {
        // 落到 fallback
      }
    }
    fallbackCopy(textToCopy);
  }, [state, fallbackCopy]);

  return (
    <Modal
      title={
        <span className="flex items-center gap-2 text-red-600">
          <XCircle className="h-5 w-5" />
          <span className="font-semibold">
            {state._msgList && state._msgList.length > 1
              ? '多项操作失败'
              : state.title || '操作失败'}
          </span>
        </span>
      }
      open={state.isOpen}
      onOk={handleClose}
      onCancel={handleClose}
      okText="知道了"
      okButtonProps={{ type: 'primary' }}
      cancelButtonProps={{ style: { display: 'none' } }}
      zIndex={9999}
    >
      <div className="space-y-3 py-4">
        <div className="max-h-60 overflow-y-auto text-sm leading-relaxed font-medium whitespace-pre-wrap text-gray-800">
          {state.message || '发生未知错误'}
        </div>

        {state.traceId && (
          <div className="flex items-center gap-2 rounded-md bg-gray-50 px-3 py-3">
            <span className="text-xs text-gray-500">追踪 ID:</span>
            <code className="font-mono text-xs text-gray-700 select-all">
              {state.traceId}
            </code>
          </div>
        )}

        {state.detail && (
          <div className="rounded-md border border-gray-200">
            <div className="flex items-center justify-between px-3 py-2 border-b border-gray-200">
              <span className="text-xs text-gray-600">详细信息</span>
              <Button
                type="text"
                size="small"
                onClick={(e) => {
                  e.stopPropagation();
                  handleCopy();
                }}
                className="h-6 px-2 text-xs"
              >
                {copied ? <Check className="h-3 w-3" /> : <Copy className="h-3 w-3" />}
                <span className="ml-1">{copied ? '已复制' : '复制'}</span>
              </Button>
            </div>
            <div className="max-h-60 overflow-y-auto p-3">
              <pre className="font-mono text-xs leading-relaxed whitespace-pre-wrap text-gray-700 m-0">
                {formatDetail(state.detail)}
              </pre>
            </div>
          </div>
        )}
      </div>
    </Modal>
  );
};

let modalContainer: HTMLDivElement | null = null;

const getModalContainer = (): HTMLDivElement => {
  if (!modalContainer || !document.body.contains(modalContainer)) {
    modalContainer = document.createElement('div');
    modalContainer.id = 'error-modal-portal';
    // 确保错误弹窗始终在最顶层，高于 Ant Design Drawer / Modal
    modalContainer.style.cssText =
      'position:fixed;top:0;left:0;width:100%;height:0;z-index:9999;pointer-events:none;';
    document.body.appendChild(modalContainer);
  }
  return modalContainer;
};

/**
 * 全局错误弹窗组件，需要在应用根组件中渲染一次
 */
export const ErrorModalProvider: React.FC = () => {
  const [mounted, setMounted] = useState(false);

  useEffect(() => {
    setMounted(true);
    return () => {
      setMounted(false);
      if (modalContainer && document.body.contains(modalContainer)) {
        document.body.removeChild(modalContainer);
        modalContainer = null;
      }
    };
  }, []);

  if (!mounted) return null;

  return createPortal(<ErrorModalContent />, getModalContainer());
};

const unknownErrorText = () => '未知错误';

/**
 * 全局错误弹窗（参考 cargo-master-ui-console 的 error-modal 实现）
 * 展示标题、错误消息、可复制的详细信息，弹窗打开期间重复错误自动去重合并
 */
export const ErrorModal = {
  show: (options: ErrorModalOptions) => {
    if (setGlobalErrorModalState) {
      setGlobalErrorModalState((prev) => {
        const now = new Date().toLocaleTimeString();
        if (prev.isOpen) {
          const nextMessage = options.message || unknownErrorText();
          const nextDetail =
            options.detail || options.message || unknownErrorText();

          // 相同 message 去重，只保留一条
          const existingMessages = prev._msgList?.length
            ? prev._msgList
            : prev.message
              ? [prev.message]
              : [];
          if (existingMessages.includes(nextMessage)) {
            return prev;
          }

          const newMessages = [...existingMessages, nextMessage];
          const numberedMessage = newMessages
            .map((msg, i) => `${i + 1}. ${msg}`)
            .join('\n\n');

          return {
            ...prev,
            message: numberedMessage,
            _msgList: newMessages,
            detail: `${prev.detail || ''}\n\n--- ${now} ---\n\n${nextDetail}`,
          };
        }
        const firstDetail =
          options.detail || options.message || unknownErrorText();
        return {
          isOpen: true,
          _msgList: [options.message || unknownErrorText()],
          traceId: options.traceId || generateTraceId(),
          ...options,
          detail: `--- ${now} ---\n\n${firstDetail}`,
        };
      });
    } else {
      pendingErrors.push(options);
    }
  },

  close: () => {
    if (setGlobalErrorModalState) {
      setGlobalErrorModalState((prev) => ({ ...prev, isOpen: false }));
    }
  },
};
